import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle, XCircle } from 'lucide-react';

export default function VerifyPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const { loginWithMagicLink } = useAuth();
  const navigate = useNavigate();
  const verifyAttempted = useRef(false);

  useEffect(() => {
    // Prevent double-call from React StrictMode
    if (verifyAttempted.current) return;
    verifyAttempted.current = true;

    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setError('No token provided');
      return;
    }

    loginWithMagicLink(token)
      .then((user) => {
        setStatus('success');
        setTimeout(() => {
          // Check if first-time login (hasn't completed onboarding)
          if (!user.onboardingCompleted) {
            navigate('/welcome');
          } else {
            navigate(user.role === 'CONTRACTOR' ? '/my/dashboard' : '/admin/dashboard');
          }
        }, 1500);
      })
      .catch((err) => {
        setStatus('error');
        setError(err.message || 'Invalid or expired link');
      });
  }, [searchParams, loginWithMagicLink, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm text-center">
        {status === 'verifying' && (
          <>
            <div className="w-12 h-12 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900">Verifying...</h1>
            <p className="text-gray-500 mt-2">Please wait while we sign you in.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">You're in!</h1>
            <p className="text-gray-500 mt-2">Redirecting...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Verification Failed</h1>
            <p className="text-gray-500 mt-2 mb-4">{error}</p>
            <a href="/magic-link" className="btn-primary">
              Request New Link
            </a>
          </>
        )}
      </div>
    </div>
  );
}
